/**
 * KR Operations Center — Passive Live Monitor (Phase 27.41 — Rich)
 *
 * Route: `/operations-center` (standalone — no sidebar, full viewport).
 *
 * Same layout as Phase 27.40, richer information density inside each
 * panel. Layout MUST NOT change:
 *   header thin → KPI row → 65/35 split (Work Queue L, Requests +
 *   Leads + Upcoming R) → footer thin.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import {
  FileText, Loader as LoaderIcon, Truck, HelpCircle, CheckCircle2,
  ListChecks, LifeBuoy, RefreshCw, Loader2, AlertCircle, CircleDot,
  Calendar, UserCheck, Users, Store, PartyPopper, ShoppingBag,
  Sparkles, Clock,
} from 'lucide-react';

const REFRESH_MS = 30_000;
const KR_LOGO = "https://customer-assets.emergentagent.com/job_35859c3c-f3c9-45c7-a4c9-9ec823ac8280/artifacts/a7ioyzyi_3%20copy.png";

// Open a detail page in a new tab so the passive monitor is never
// interrupted. Guards a rel policy that matches security best practice.
function openInNewTab(path) {
  if (!path) return;
  window.open(path, '_blank', 'noopener,noreferrer');
}

// ── Data hook ────────────────────────────────────────────────
function useLivePulse() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);
  const fetchOnce = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await api.get('/operations/pulse');
      setData(res.data); setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load Operations Pulse');
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => {
    fetchOnce();
    timerRef.current = setInterval(() => fetchOnce(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);
  return { data, error, loading, refreshing };
}

// ── Small colored icon container ─────────────────────────────
function IconBadge({ icon: Icon, tone = 'slate' }) {
  const map = {
    slate:   'bg-slate-100  text-slate-600',
    navy:    'bg-[#0f2547]/10 text-[#0f2547]',
    sky:     'bg-sky-100    text-sky-700',
    amber:   'bg-amber-100  text-amber-700',
    rose:    'bg-rose-100   text-rose-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    violet:  'bg-violet-100 text-violet-700',
    orange:  'bg-orange-100 text-orange-700',
    gold:    'bg-[#f5c14f]/20 text-[#8a6510]',
  };
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md shrink-0 ${map[tone] || map.slate}`}>
      <Icon size={13} strokeWidth={2.2} />
    </span>
  );
}

// ── KPI tile — polished ──────────────────────────────────────
function KpiTile({ label, value, icon, tone = 'slate' }) {
  const zero = !value;
  const ring =
    zero          ? 'ring-slate-200 bg-white' :
    tone === 'rose'    ? 'ring-rose-200 bg-gradient-to-b from-rose-50/60 to-white' :
    tone === 'amber'   ? 'ring-amber-200 bg-gradient-to-b from-amber-50/60 to-white' :
    tone === 'emerald' ? 'ring-emerald-200 bg-gradient-to-b from-emerald-50/60 to-white' :
    tone === 'sky'     ? 'ring-sky-200 bg-gradient-to-b from-sky-50/60 to-white' :
    tone === 'gold'    ? 'ring-[#f5c14f]/40 bg-gradient-to-b from-[#f5c14f]/10 to-white' :
    'ring-slate-200 bg-white';
  return (
    <div
      className={`rounded-xl ring-1 ${ring} shadow-sm px-3 py-2 flex items-center justify-between gap-2`}
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
    >
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 truncate">{label}</span>
        <span className={`text-2xl font-bold tabular-nums ${zero ? 'text-slate-300' : 'text-[#0f2547]'}`}>{value ?? 0}</span>
      </div>
      <IconBadge icon={icon} tone={zero ? 'slate' : tone} />
    </div>
  );
}

// ── Card wrapper ─────────────────────────────────────────────
function Section({ title, icon, iconTone = 'navy', right, children, className = '', testid }) {
  return (
    <div
      className={`bg-white rounded-xl ring-1 ring-slate-200 shadow-sm px-3 py-2.5 flex flex-col min-h-0 ${className}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {icon && <IconBadge icon={icon} tone={iconTone} />}
          <h2 className="text-[11px] uppercase tracking-widest font-bold text-[#0f2547] truncate">{title}</h2>
        </div>
        {right}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

// ── Status pill ──────────────────────────────────────────────
function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  const cls =
    s.includes('missing') || s.includes('info_requested') ? 'bg-amber-100 text-amber-800 ring-amber-200' :
    s.includes('submitted') ? 'bg-sky-100 text-sky-800 ring-sky-200' :
    s.includes('review')  ? 'bg-violet-100 text-violet-800 ring-violet-200' :
    s.includes('pending') ? 'bg-slate-100 text-slate-700 ring-slate-200' :
    s.includes('hold')    ? 'bg-orange-100 text-orange-800 ring-orange-200' :
    s.includes('carrier') ? 'bg-blue-100 text-blue-800 ring-blue-200' :
    s.includes('release') ? 'bg-emerald-100 text-emerald-800 ring-emerald-200' :
    s.includes('paid')    ? 'bg-emerald-100 text-emerald-800 ring-emerald-200' :
    s.includes('processing') || s.includes('print') ? 'bg-sky-100 text-sky-800 ring-sky-200' :
    s.includes('approved') ? 'bg-emerald-100 text-emerald-800 ring-emerald-200' :
    s.includes('ready')   ? 'bg-emerald-100 text-emerald-800 ring-emerald-200' :
    s.includes('auto')    ? 'bg-teal-100 text-teal-800 ring-teal-200' :
    'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold ring-1 ${cls} truncate`}>
      {status || '—'}
    </span>
  );
}

// ── Work Queue ───────────────────────────────────────────────
function WorkQueue({ rows }) {
  if (!rows?.length) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-slate-400" data-testid="work-queue-empty">
        No pending contracting work.
      </div>
    );
  }
  return (
    <div className="overflow-hidden" data-testid="work-queue">
      <div className="grid grid-cols-[1.5fr_1.4fr_130px_60px_80px] gap-x-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold pb-1.5 border-b border-slate-200">
        <span>Agent</span>
        <span>Carrier · Product</span>
        <span>Status</span>
        <span className="text-right">Days</span>
        <span className="text-right">Last Activity</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li
            key={`${r.request_id}-${i}`}
            className="grid grid-cols-[1.5fr_1.4fr_130px_60px_80px] gap-x-3 items-center py-1.5 text-sm hover:bg-[#0f2547]/[0.03] transition-colors cursor-pointer group"
            data-testid={`work-row-${i}`}
            onClick={() => openInNewTab(`/requests/${r.request_id}`)}
            title="Open contract request in a new tab"
          >
            <div className="min-w-0 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0f2547]/50 shrink-0 group-hover:bg-[#f5c14f] transition-colors" />
              <span className="text-[#0f2547] font-semibold truncate group-hover:underline decoration-[#f5c14f] decoration-2 underline-offset-2">{r.agent_name || '—'}</span>
            </div>
            <div className="min-w-0 flex flex-col leading-tight">
              <span className="text-slate-800 truncate text-[13px]">{r.carrier_name || '—'}</span>
              <span className="text-[11px] text-slate-500 truncate">{r.product || '—'}</span>
            </div>
            <StatusPill status={r.status} />
            <span className="tabular-nums font-bold text-rose-600 text-right">{r.days_pending ?? 0}d</span>
            <span className="tabular-nums text-slate-500 text-right text-[12px]">
              {(r.last_activity_days ?? 0) === 0 ? 'today' : `${r.last_activity_days}d ago`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Requests Pending ─────────────────────────────────────────
const REQUEST_META = {
  marketing: { label: 'Marketing Requests', icon: FileText,   tone: 'sky'    },
  retail:    { label: 'Retail Programs',    icon: Store,      tone: 'violet' },
  events:    { label: 'Event Requests',     icon: PartyPopper,tone: 'gold'   },
  orders:    { label: 'Online Orders',      icon: ShoppingBag,tone: 'emerald'},
};

function RequestCategory({ slug, group }) {
  const meta = REQUEST_META[slug];
  const empty = !group?.count;
  return (
    <div data-testid={`requests-${slug}`} className="border-t border-slate-100 first:border-t-0 pt-2 first:pt-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <IconBadge icon={meta.icon} tone={empty ? 'slate' : meta.tone} />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#0f2547] truncate">{meta.label}</span>
        </div>
        <span className={`text-base font-bold tabular-nums ${empty ? 'text-slate-300' : 'text-[#0f2547]'}`}>{group?.count ?? 0}</span>
      </div>
      {!empty && group.oldest?.length > 0 && (
        <ul className="mt-1 space-y-0.5 pl-8">
          {group.oldest.map((it, i) => (
            <li key={`${slug}-${i}`} className="text-[11px] text-slate-600 flex items-baseline gap-1.5 truncate">
              <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
              <span className="text-slate-800 font-medium truncate">{it.agent}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-600 truncate">{it.what}</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-500 tabular-nums whitespace-nowrap">
                {it.event_date ? it.event_date : `${it.days}d`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestsPending({ data }) {
  if (!data) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="requests-pending">
      {['marketing', 'retail', 'events', 'orders'].map((slug) => (
        <RequestCategory key={slug} slug={slug} group={data[slug]} />
      ))}
    </div>
  );
}

// ── Leads Pulse (metrics + Needs Follow-up) ──────────────────
function LeadsPulse({ metrics }) {
  const rows = [
    { key: 'new_unworked',       label: 'New / Unworked',     tone: 'sky' },
    { key: 'untouched_24h',      label: 'Untouched > 24h',    tone: 'amber' },
    { key: 'followups_due',      label: 'Follow-ups Due',     tone: 'navy' },
    { key: 'overdue_followups',  label: 'Overdue Follow-ups', tone: 'rose' },
    { key: 'appointments_today', label: 'Appointments Today', tone: 'emerald' },
  ];
  const needs = metrics?.needs_followup || [];
  return (
    <div className="flex flex-col gap-2" data-testid="leads-pulse">
      <ul className="grid grid-cols-1 gap-0.5">
        {rows.map(({ key, label, tone }) => {
          const n = metrics?.[key] ?? 0;
          const dot =
            n === 0 ? 'bg-slate-200' :
            tone === 'rose'    ? 'bg-rose-500' :
            tone === 'amber'   ? 'bg-amber-500' :
            tone === 'emerald' ? 'bg-emerald-500' :
            tone === 'sky'     ? 'bg-sky-500' :
            'bg-[#0f2547]';
          return (
            <li key={key} className="flex items-center justify-between text-xs" data-testid={`leads-${key}`}>
              <span className="flex items-center gap-2 text-slate-700">
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                {label}
              </span>
              <span className={`tabular-nums font-bold ${n ? 'text-[#0f2547]' : 'text-slate-300'}`}>{n}</span>
            </li>
          );
        })}
      </ul>
      {needs.length > 0 && (
        <div className="mt-1 pt-2 border-t border-slate-100" data-testid="leads-needs-followup">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={11} className="text-[#8a6510]" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#0f2547]">Needs Follow-up</span>
          </div>
          <ul className="space-y-1">
            {needs.map((l, i) => {
              const sevDot =
                l.severity === 'critical' ? 'bg-rose-500' :
                l.severity === 'warning'  ? 'bg-amber-500' : 'bg-sky-500';
              const sevBadge =
                l.severity === 'critical' ? 'bg-rose-100 text-rose-700 ring-rose-200' :
                l.severity === 'warning'  ? 'bg-amber-100 text-amber-800 ring-amber-200' :
                'bg-sky-100 text-sky-700 ring-sky-200';
              return (
                <li
                  key={`${l.lead_id}-${i}`}
                  className="leading-tight cursor-pointer group rounded-md px-1.5 -mx-1.5 py-1 hover:bg-[#0f2547]/[0.04] transition-colors"
                  data-testid={`needs-followup-${i}`}
                  onClick={() => openInNewTab(`/leads/${l.lead_id}`)}
                  title="Open lead in a new tab"
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sevDot}`} />
                    <span className="text-[9px] uppercase tracking-widest font-bold text-slate-400 shrink-0">Lead</span>
                    <span className="font-semibold text-[#0f2547] truncate text-[12px] group-hover:underline decoration-[#f5c14f] decoration-2 underline-offset-2">
                      {l.lead_name}
                    </span>
                  </div>
                  <div className="pl-3 flex items-center gap-1.5 text-[10px] leading-tight">
                    <span className="uppercase tracking-widest font-bold text-slate-400 shrink-0">Agent</span>
                    <span className="text-slate-700 truncate">{l.assigned_to}</span>
                  </div>
                  <div className="pl-3 mt-0.5">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold ring-1 ${sevBadge}`}>
                      {l.reason}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Upcoming ─────────────────────────────────────────────────
function Upcoming({ items }) {
  if (!items?.length) {
    return (
      <div className="flex items-center justify-center py-3 text-xs text-slate-400 italic" data-testid="upcoming-empty">
        Nothing scheduled.
      </div>
    );
  }
  return (
    <ul className="space-y-1.5" data-testid="upcoming">
      {items.map((u, i) => (
        <li key={`${u.title}-${i}`} className="flex items-center gap-2 text-xs">
          <div className="flex flex-col items-center text-[10px] leading-tight bg-[#f5c14f]/15 text-[#8a6510] rounded-md px-1.5 py-0.5 shrink-0 min-w-[42px]">
            <span className="font-bold uppercase tracking-wide">
              {u.date ? new Date(u.date).toLocaleDateString('en-US', { month: 'short' }) : '—'}
            </span>
            <span className="font-bold text-xs">
              {u.date ? new Date(u.date).getDate() : ''}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[#0f2547] font-medium truncate leading-tight">{u.title}</div>
            <div className="text-[10px] text-slate-500">
              {u.type} · in {u.days_until ?? 0}d
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Page ─────────────────────────────────────────────────────
export default function OperationsCenterPage() {
  const { data, error, loading, refreshing } = useLivePulse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="operations-loading">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3">
        <AlertCircle size={32} className="text-rose-400" />
        <p className="text-slate-600">{error}</p>
      </div>
    );
  }
  if (!data) return null;

  const {
    today_kpis = {}, work_queue = [], requests_pending = {},
    leads_pulse = {}, kr_upcoming = [], last_updated,
  } = data;
  const lastFmt = last_updated ? new Date(last_updated).toLocaleTimeString() : '—';

  return (
    <div
      className="h-screen w-full flex flex-col gap-2 px-4 py-3 overflow-hidden relative"
      style={{
        background:
          'radial-gradient(1200px 400px at 0% 0%, rgba(15,37,71,0.06), transparent 60%),' +
          'radial-gradient(900px 400px at 100% 0%, rgba(245,193,79,0.08), transparent 60%),' +
          'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      }}
      data-testid="operations-center-page"
    >
      {/* Header — premium branded bar */}
      <header
        className="flex items-center justify-between shrink-0 rounded-xl ring-1 ring-[#0f2547]/10 shadow-md px-4 py-2.5 relative overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #0f2547 0%, #14335f 55%, #0f2547 100%)',
        }}
      >
        {/* Subtle gold accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px]"
             style={{ background: 'linear-gradient(90deg, transparent 0%, #f5c14f 20%, #f5c14f 80%, transparent 100%)' }} />
        <div className="flex items-center gap-4 min-w-0">
          <img
            src={KR_LOGO}
            alt="KR Enterprise Group"
            className="h-9 w-auto object-contain drop-shadow-md"
            data-testid="ops-header-logo"
          />
          <div className="hidden sm:block h-8 w-px bg-white/15" />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#f5c14f]">
              KR Enterprise Group
            </span>
            <h1 className="text-lg font-heading font-bold text-white tracking-tight">
              Operations Center
            </h1>
          </div>
          <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-200 bg-emerald-500/15 ring-1 ring-emerald-400/40 rounded-full px-2 py-0.5 font-semibold ml-2">
            <CircleDot size={9} className="text-emerald-300 animate-pulse" /> Live
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-200/80" data-testid="last-updated">
          <RefreshCw size={12} className={refreshing ? 'animate-spin text-[#f5c14f]' : 'text-slate-300/60'} />
          <span className="tabular-nums">Updated {lastFmt}</span>
        </div>
      </header>

      {/* Row 1 — Today's Numbers (7 KPIs) */}
      <div className="grid grid-cols-7 gap-2 shrink-0" data-testid="kpi-row">
        <KpiTile label="New Requests"     value={today_kpis.new_requests}     icon={FileText}    tone="navy" />
        <KpiTile label="In Progress"      value={today_kpis.in_progress}      icon={LoaderIcon}  tone="sky" />
        <KpiTile label="Awaiting Carrier" value={today_kpis.awaiting_carrier} icon={Truck}       tone="sky" />
        <KpiTile label="Missing Info"     value={today_kpis.missing_info}     icon={HelpCircle}  tone="amber" />
        <KpiTile label="Approved Today"   value={today_kpis.approved_today}   icon={CheckCircle2}tone="emerald" />
        <KpiTile label="Overdue Tasks"    value={today_kpis.overdue_tasks}    icon={ListChecks}  tone="rose" />
        <KpiTile label="Open Tickets"     value={today_kpis.open_tickets}     icon={LifeBuoy}    tone="gold" />
      </div>

      {/* Row 2 — 65/35 split */}
      <div className="grid grid-cols-[65fr_35fr] gap-2 flex-1 min-h-0">
        <Section
          title="Work Queue · Oldest Pending"
          icon={ListChecks}
          iconTone="navy"
          testid="section-work-queue"
          right={<span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 tabular-nums">{work_queue.length}</span>}
          className="min-h-0"
        >
          <WorkQueue rows={work_queue} />
        </Section>

        <div className="flex flex-col gap-2 min-h-0">
          <Section
            title="Requests Pending"
            icon={Users}
            iconTone="navy"
            testid="section-requests-pending"
            className="flex-none"
          >
            <RequestsPending data={requests_pending} />
          </Section>
          <Section
            title="Leads Pulse"
            icon={Sparkles}
            iconTone="gold"
            testid="section-leads-pulse"
            className="flex-none"
          >
            <LeadsPulse metrics={leads_pulse} />
          </Section>
          <Section
            title="Upcoming · Next 7 Days"
            icon={Calendar}
            iconTone="sky"
            testid="section-kr-upcoming"
            className="flex-none"
          >
            <Upcoming items={kr_upcoming} />
          </Section>
          <div className="flex-1 min-h-0" aria-hidden="true" />
        </div>
      </div>

      {/* Footer — thin premium bar */}
      <footer
        className="flex items-center justify-between shrink-0 text-[11px] rounded-lg ring-1 ring-[#0f2547]/10 bg-white/70 backdrop-blur-sm px-3 py-1.5 shadow-sm"
        data-testid="operations-footer"
      >
        <span className="flex items-center gap-2 text-[#0f2547]/70">
          <UserCheck size={11} className="text-[#0f2547]/50" />
          <span className="font-semibold">KR Operations</span>
          <span className="text-slate-400">·</span>
          <span>Passive monitor · Click any row to open detail in a new tab</span>
        </span>
        <span className="flex items-center gap-3 text-slate-500">
          <span className="flex items-center gap-1">
            <Clock size={10} className="text-slate-400" />
            Last Updated <span className="tabular-nums text-[#0f2547]/70">{lastFmt}</span>
          </span>
          <span className="text-slate-300">·</span>
          <span>Auto-refresh <span className="text-[#0f2547]/70 font-semibold">30s</span></span>
        </span>
      </footer>
    </div>
  );
}
